import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL, SITE_NAME, SITE_LOGO } from '@/utils/seo';

const articleSlug = 'oopbuy-spreadsheet-guide';
const articleUrl = `${SITE_URL}/${articleSlug}`;

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      headline: 'OOPBuy Spreadsheet 2026: Die komplette Anleitung für Deutschland',
      description: 'Alles, was du über das OOPBuy Spreadsheet wissen musst – von der ersten Produktsuche über Qualitätskontrollen bis hin zum sicheren Versand nach Deutschland.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20an%20open%20laptop%20displaying%20a%20colorful%20organized%20spreadsheet%20surrounded%20by%20carefully%20placed%20sneakers%20and%20fashion%20items%20warm%20natural%20light%20from%20side%20window%20professional%20editorial%20product%20photography%20cream%20and%20taupe%20tones%20clean%20airy%20composition&width=1600&height=800&seq=guide-hero-v3-04&orientation=landscape',
      url: articleUrl,
      datePublished: '2026-07-01',
      dateModified: '2026-07-15',
      author: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: SITE_LOGO },
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: { '@type': 'ImageObject', url: SITE_LOGO },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      inLanguage: 'de-DE',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Startseite', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'OOPBuy Spreadsheet Guide', item: articleUrl },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Ist das OOPBuy Spreadsheet kostenlos?',
          acceptedAnswer: { '@type': 'Answer', text: 'Ja, das OOPBuy Spreadsheet ist komplett kostenlos. Du kannst alle Produktlisten, Kategorien und Guides ohne Registrierung oder versteckte Kosten nutzen.' },
        },
        {
          '@type': 'Question',
          name: 'Wie oft werden die Produkte aktualisiert?',
          acceptedAnswer: { '@type': 'Answer', text: 'Die Produktdatenbank wird regelmäßig – teilweise täglich – aktualisiert. Neue Trendprodukte werden schnell hinzugefügt, nicht mehr verfügbare Links entfernt.' },
        },
        {
          '@type': 'Question',
          name: 'Kann ich Produkte auch ohne Agent bestellen?',
          acceptedAnswer: { '@type': 'Answer', text: 'Theoretisch ja, aber wir empfehlen einen Agenten wie OOPBuy. Der Agent prüft die Qualität, macht QC-Fotos und kümmert sich um den Versand – das ist sicherer und oft günstiger.' },
        },
        {
          '@type': 'Question',
          name: 'Wie hoch sind die Versandkosten nach Deutschland?',
          acceptedAnswer: { '@type': 'Answer', text: 'Die Versandkosten hängen von Gewicht, Größe und Versandart ab. Mit DHL Europe Line beginnt der Versand bei etwa 15-25 € für ein Paar Schuhe.' },
        },
        {
          '@type': 'Question',
          name: 'Funktioniert OOPBuy Spreadsheet auch für Österreich und Schweiz?',
          acceptedAnswer: { '@type': 'Answer', text: 'Ja, die meisten Informationen und Versandoptionen gelten auch für Österreich und die Schweiz. Einige Zollbestimmungen können abweichen.' },
        },
      ],
    },
  ],
};

export default function GuideArticle() {
  return (
    <main className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(guideJsonLd) }}
      />
      <Navbar />

      <article className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors whitespace-nowrap">
              Startseite
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">
              OOPBuy Spreadsheet Guide
            </span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-semibold text-accent-600 bg-accent-100 px-3 py-1 rounded-full">Guide</span>
              <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-3 py-1 rounded-full">2026</span>
              <span className="text-xs font-semibold text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full">Deutschland</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 leading-tight">
              OOPBuy Spreadsheet 2026: Die komplette Anleitung für Deutschland
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              Alles, was du über das <strong>OOPBuy Spreadsheet</strong> wissen musst – von der ersten Produktsuche über 
              Qualitätskontrollen bis hin zum sicheren Versand nach Deutschland. Dieser Guide ist speziell für 
              deutschsprachige Käufer geschrieben, die <strong>sicher und günstig aus China einkaufen</strong> möchten.
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                Aktualisiert: Juli 2026
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                Lesezeit: 12 Minuten
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20an%20open%20laptop%20displaying%20a%20colorful%20organized%20spreadsheet%20surrounded%20by%20carefully%20placed%20sneakers%20and%20fashion%20items%20warm%20natural%20light%20from%20side%20window%20professional%20editorial%20product%20photography%20cream%20and%20taupe%20tones%20clean%20airy%20composition&width=1600&height=800&seq=guide-hero-v3-04&orientation=landscape"
              alt="OOPBuy Spreadsheet Anleitung Deutschland"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-xl border border-background-200/70 p-6 mb-10">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Inhaltsverzeichnis</h2>
            <ol className="flex flex-col gap-2 text-sm">
              {[
                'Was ist ein OOPBuy Spreadsheet?',
                'Warum brauchst du ein OOPBuy Spreadsheet?',
                'Wie funktioniert das OOPBuy Spreadsheet?',
                'Die besten Produktkategorien im Überblick',
                'QC-Bilder richtig verstehen und bewerten',
                'Versand nach Deutschland: Alle Optionen erklärt',
                'OOPBuy Spreadsheet vs andere Plattformen',
                'Sicherheits-Tipps für den Einkauf aus China',
                'Häufig gestellte Fragen zum OOPBuy Spreadsheet',
                'Fazit: Lohnt sich das OOPBuy Spreadsheet?',
              ].map((title, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-accent-500 font-semibold flex-shrink-0">{idx + 1}.</span>
                  <a
                    href={`#section-${idx + 1}`}
                    className="text-foreground-700 hover:text-accent-500 transition-colors"
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Section 1 */}
          <section id="section-1" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Was ist ein OOPBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Ein <strong>OOPBuy Spreadsheet</strong> ist eine umfassende, strukturierte Tabelle mit geprüften 
              Produktlinks aus chinesischen Online-Shops wie <strong>Taobao</strong>, <strong>Weidian</strong> 
              und <strong>1688</strong>. Stell dir vor, du hättest eine persönliche Einkaufsliste mit tausenden 
              beliebten Artikeln, die bereits von Experten auf Qualität, Preis und Verfügbarkeit geprüft wurden.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Das Besondere am OOPBuy Spreadsheet: Es enthält nicht nur Produktlinks, sondern auch 
              <strong>QC-Bilder</strong> (Qualitätskontrollfotos), Verkäuferbewertungen und Preisvergleiche. 
              Für deutschsprachige Nutzer ist es die einfachste Methode, hochwertige Produkte aus China zu 
              finden, ohne stundenlang verschiedene Plattformen durchsuchen zu müssen.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              Der Hauptvorteil liegt in der <strong>Zeitersparnis und Sicherheit</strong>. Anstatt wildfremde 
              Shops zu durchstöbern, greifst du auf eine kuratierte Liste zu, die regelmäßig aktualisiert wird. 
              Zurück zur{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                Startseite
              </Link>{' '}
              für einen Gesamtüberblick.
            </p>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Warum brauchst du ein OOPBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Viele Käufer stehen vor denselben Problemen: <strong>Unübersichtliche Produktseiten</strong>, 
              gefälschte Bewertungen und unklare Versandkosten. Das OOPBuy Spreadsheet löst diese Probleme 
              auf elegante Weise.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[
                { title: 'Zeitersparnis', desc: 'Kein stundenlanges Suchen – alle beliebten Produkte sind übersichtlich in Kategorien sortiert.' },
                { title: 'Geprüfte Qualität', desc: 'Jeder Eintrag enthält QC-Bilder und Hinweise zur Produktqualität.' },
                { title: 'Preisvergleich', desc: 'Siehe auf einen Blick, welcher Verkäufer den besten Preis bietet.' },
                { title: 'Deutsche Anleitung', desc: 'Versand, Zoll und Zahlung werden speziell für Deutschland erklärt.' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Wie funktioniert das OOPBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Die Nutzung ist denkbar einfach und in <strong>vier Schritten</strong> erledigt. Selbst wenn du 
              noch nie aus China bestellt hast, wirst du mit dem OOPBuy Spreadsheet schnell zurechtkommen.
            </p>
            <div className="flex flex-col gap-4 mt-4">
              {[
                { step: '1.', title: 'Produkt finden', desc: 'Durchsuche die Kategorien nach Schuhen, Kleidung, Accessoires oder Elektronik. Jede Kategorie enthält hunderte geprüfte Einträge mit direkten Links.' },
                { step: '2.', title: 'QC-Bilder prüfen', desc: 'Klicke auf ein Produkt und schaue dir die Qualitätskontrollbilder an. So weißt du genau, was du bekommst – vor dem Kauf.' },
                { step: '3.', title: 'Preis vergleichen', desc: 'Vergleiche Preise verschiedener Verkäufer in der Tabelle. Das Spreadsheet zeigt dir die günstigste Option mit der besten Bewertung.' },
                { step: '4.', title: 'Sicher bestellen', desc: 'Kopiere den Link und bestelle über OOPBuy. Unsere Guides erklären jeden Schritt – von der Zahlung bis zum Versand nach Deutschland.' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <span className="text-lg font-bold text-accent-500 flex-shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="text-sm text-foreground-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Die besten Produktkategorien im Überblick
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Das OOPBuy Spreadsheet deckt eine riesige Bandbreite an Produkten ab. Hier sind die 
              <strong>beliebtesten Kategorien</strong> für deutschsprachige Käufer:
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-secondary-100">
                    <th className="text-left px-4 py-3 font-semibold text-foreground-900 rounded-tl-lg">Kategorie</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-900">Beliebtheit</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-900 rounded-tr-lg">Typische Produkte</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { cat: 'Sneaker', pop: '★★★★★', examples: 'Nike, Jordan, Yeezy, Dunks' },
                    { cat: 'Hoodies & Sweater', pop: '★★★★★', examples: 'Streetwear, Vintage, Designer' },
                    { cat: 'T-Shirts', pop: '★★★★☆', examples: 'Oversized, Graphic, Basic' },
                    { cat: 'Jacken', pop: '★★★★☆', examples: 'Winterjacken, Windbreaker, Leder' },
                    { cat: 'Taschen', pop: '★★★★★', examples: 'Rucksäcke, Umhängetaschen, Totes' },
                    { cat: 'Accessoires', pop: '★★★★☆', examples: 'Schmuck, Uhren, Kappen, Gürtel' },
                    { cat: 'Sets', pop: '★★★☆☆', examples: 'Tracksuits, Koordinaten-Sets' },
                    { cat: 'Elektronik', pop: '★★★☆☆', examples: 'Kopfhörer, Gadgets, Zubehör' },
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                      <td className="px-4 py-3 font-medium text-foreground-800">{row.cat}</td>
                      <td className="px-4 py-3 text-accent-500">{row.pop}</td>
                      <td className="px-4 py-3 text-foreground-600">{row.examples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-foreground-500 mt-3">
              Alle Kategorien findest du übersichtlich auf unserer{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                Startseite
              </Link>.
            </p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              QC-Bilder richtig verstehen und bewerten
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              QC steht für <strong>Quality Control</strong>. Bevor deine Bestellung aus China versendet wird, 
              macht der Agent Fotos von jedem Produkt. Diese QC-Bilder sind deine Chance, die Qualität zu 
              prüfen, bevor das Paket auf die Reise geht.
            </p>
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 mt-4">
              <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line text-accent-500"></i>
                Worauf du bei QC-Bildern achten solltest
              </h3>
              <ul className="flex flex-col gap-2 text-sm text-foreground-700">
                {[
                  'Vergleiche das Produkt mit den Bildern aus dem Original-Shop.',
                  'Achte auf Nähte, Logos und Materialqualität.',
                  'Prüfe Farben – Beleuchtung kann täuschen.',
                  'Bei Schuhen: Vergleiche die Sohle und das Innenfutter.',
                  'Fordere bei Unklarheiten zusätzliche Fotos an.',
                ].map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-accent-500 mt-0.5">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed mt-4">
              Mehr Details zu diesem Thema findest du im{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                vollständigen OOPBuy Spreadsheet Überblick
              </Link>.
            </p>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Versand nach Deutschland: Alle Optionen erklärt
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Der <strong>Versand nach Deutschland</strong> ist einer der wichtigsten Aspekte beim Einkauf 
              über OOPBuy. Hier eine Übersicht der gängigsten Versandoptionen:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {[
                { name: 'DHL Europe Line', time: '8-14 Tage', cost: 'Mittel', rating: '★★★★★', desc: 'Beste Wahl für Deutschland. Zuverlässig, Tracking inklusive, zolloptimiert.' },
                { name: 'EUB / EMS', time: '10-20 Tage', cost: 'Günstig', rating: '★★★☆☆', desc: 'Preiswerte Option für kleinere Pakete. Längere Lieferzeit, einfaches Tracking.' },
                { name: 'DPD / GLS', time: '7-12 Tage', cost: 'Mittel-Hoch', rating: '★★★★☆', desc: 'Schnelle Lieferung, gute Sendungsverfolgung, etwas teurer.' },
              ].map((option, idx) => (
                <div key={idx} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-900">{option.name}</h3>
                  <div className="mt-3 flex flex-col gap-1 text-xs text-foreground-600">
                    <span className="flex items-center gap-2">
                      <i className="ri-time-line text-foreground-400"></i> {option.time}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-money-euro-circle-line text-foreground-400"></i> {option.cost}
                    </span>
                    <span className="text-accent-500">{option.rating}</span>
                  </div>
                  <p className="text-xs text-foreground-600 mt-2">{option.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
              <p className="text-xs text-foreground-700 flex items-start gap-2">
                <i className="ri-error-warning-line text-yellow-600 mt-0.5"></i>
                <span><strong>Zoll-Hinweis:</strong> Bei Bestellungen über 150 € können zusätzliche Einfuhrabgaben anfallen. 
                Das OOPBuy Spreadsheet enthält Tipps zur Zolloptimierung. Weitere Infos auf der{' '}
                <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                  Startseite
                </Link>.</span>
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              OOPBuy Spreadsheet vs andere Plattformen
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Wie schneidet das OOPBuy Spreadsheet im Vergleich zu anderen Lösungen ab? Hier eine ehrliche 
              <strong>Gegenüberstellung</strong> der wichtigsten Kriterien:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse mt-4">
                <thead>
                  <tr className="bg-secondary-100">
                    <th className="text-left px-4 py-3 font-semibold text-foreground-900 rounded-tl-lg">Kriterium</th>
                    <th className="text-center px-4 py-3 font-semibold text-primary-700">OOPBuy Spreadsheet</th>
                    <th className="text-center px-4 py-3 font-semibold text-foreground-600">Einzelsuche</th>
                    <th className="text-center px-4 py-3 font-semibold text-foreground-600 rounded-tr-lg">Foren/Reddit</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { crit: 'Produktanzahl', oop: '★★★★★', s1: '★★★☆☆', s2: '★★☆☆☆' },
                    { crit: 'Qualitätsprüfung', oop: '★★★★★', s1: '★★☆☆☆', s2: '★★★☆☆' },
                    { crit: 'Deutsche Infos', oop: '★★★★★', s1: '★☆☆☆☆', s2: '★★☆☆☆' },
                    { crit: 'Aktualität', oop: '★★★★★', s1: '★★★☆☆', s2: '★★☆☆☆' },
                    { crit: 'Preisvergleich', oop: '★★★★★', s1: '★★☆☆☆', s2: '★☆☆☆☆' },
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                      <td className="px-4 py-3 font-medium text-foreground-800">{row.crit}</td>
                      <td className="px-4 py-3 text-center text-accent-500">{row.oop}</td>
                      <td className="px-4 py-3 text-center text-foreground-400">{row.s1}</td>
                      <td className="px-4 py-3 text-center text-foreground-400">{row.s2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Sicherheits-Tipps für den Einkauf aus China
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Sicherheit steht an erster Stelle. Mit diesen <strong>bewährten Tipps</strong> vermeidest du 
              die häufigsten Fehler beim Einkauf über OOPBuy:
            </p>
            <div className="flex flex-col gap-3 mt-4">
              {[
                { icon: 'ri-shield-check-line', title: 'Nur geprüfte Verkäufer wählen', desc: 'Das OOPBuy Spreadsheet listet ausschließlich Verkäufer mit guten Bewertungen und nachweislicher Qualität. Finger weg von Shops ohne Historie.' },
                { icon: 'ri-bank-card-line', title: 'Sichere Zahlungsmethoden nutzen', desc: 'Bezahle immer über die OOPBuy-Plattform – niemals direkt an Verkäufer. So bist du durch den Käuferschutz abgesichert.' },
                { icon: 'ri-file-text-line', title: 'Bestellungen dokumentieren', desc: 'Speichere Screenshots von Produktseiten und Bestellbestätigungen. Bei Problemen hast du so alle Nachweise parat.' },
                { icon: 'ri-global-line', title: 'Versandoption mit Tracking wählen', desc: 'Immer eine Versandart mit Sendungsverfolgung wählen. So weißt du genau, wo sich dein Paket befindet.' },
              ].map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${tip.icon} text-primary-600`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{tip.title}</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 9 - FAQ */}
          <section id="section-9" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Häufig gestellte Fragen zum OOPBuy Spreadsheet
            </h2>
            <div className="flex flex-col gap-3 mt-4">
              {[
                { q: 'Ist das OOPBuy Spreadsheet kostenlos?', a: 'Ja, das OOPBuy Spreadsheet ist komplett kostenlos. Du kannst alle Produktlisten, Kategorien und Guides ohne Registrierung oder versteckte Kosten nutzen.' },
                { q: 'Wie oft werden die Produkte aktualisiert?', a: 'Die Produktdatenbank wird regelmäßig – teilweise täglich – aktualisiert. Neue Trendprodukte werden schnell hinzugefügt, nicht mehr verfügbare Links entfernt.' },
                { q: 'Kann ich Produkte auch ohne Agent bestellen?', a: 'Theoretisch ja, aber wir empfehlen einen Agenten wie OOPBuy. Der Agent prüft die Qualität, macht QC-Fotos und kümmert sich um den Versand – das ist sicherer und oft günstiger.' },
                { q: 'Wie hoch sind die Versandkosten nach Deutschland?', a: 'Die Versandkosten hängen von Gewicht, Größe und Versandart ab. Mit DHL Europe Line beginnt der Versand bei etwa 15-25 € für ein Paar Schuhe. Das Spreadsheet enthält eine Versandkosten-Übersicht.' },
                { q: 'Funktioniert OOPBuy Spreadsheet auch für Österreich und Schweiz?', a: 'Ja, die meisten Informationen und Versandoptionen gelten auch für Österreich und die Schweiz. Einige Zollbestimmungen können abweichen – prüfe das bitte individuell.' },
              ].map((faq, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 10 - Conclusion */}
          <section id="section-10" className="mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
              Fazit: Lohnt sich das OOPBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Ganz klar: <strong>Ja, das OOPBuy Spreadsheet lohnt sich</strong>. Für deutschsprachige Käufer, 
              die Produkte aus China bestellen möchten, ist es aktuell die beste verfügbare Ressource. Die 
              Kombination aus kuratierten Produktlisten, QC-Bildern, Preisvergleichen und deutschen Guides 
              macht den Einkauf <strong>sicherer, schneller und günstiger</strong>.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Besonders für Einsteiger ist das OOPBuy Spreadsheet Gold wert. Statt sich durch unübersichtliche 
              chinesische Shop-Seiten zu kämpfen oder auf veraltete Forenbeiträge zu vertrauen, erhältst du 
              eine stets aktuelle, geprüfte Übersicht – und das komplett kostenlos.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              <strong>Bereit loszulegen?</strong> Besuche die{' '}
              <Link to="/" className="text-accent-600 hover:text-accent-700 underline font-medium">
                OOPBuy Spreadsheet Startseite
              </Link>{' '}
              für den vollen Zugriff auf alle Kategorien, Guides und die aktuellsten Produktlisten.
            </p>
          </section>

          {/* CTA Bottom */}
          <div className="bg-primary-500 rounded-2xl p-8 text-center">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-background-50">
              Starte jetzt mit dem OOPBuy Spreadsheet
            </h3>
            <p className="mt-2 text-sm text-background-50/80 max-w-lg mx-auto">
              Entdecke tausende geprüfte Produkte, QC-Bilder und deutsche Einkaufsanleitungen – alles an einem Ort.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-700 font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-line"></i>
                OOPBuy Spreadsheet entdecken
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 font-semibold text-sm hover:bg-primary-700 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                Zurück zur Startseite
              </Link>
            </div>
          </div>

          {/* Related Links */}
          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Verwandte Inhalte</h3>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap">
                OOPBuy Spreadsheet Startseite
              </Link>
              <Link to="/" className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap">
                OOPBuy Produktkategorien
              </Link>
              <Link to="/" className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap">
                OOPBuy Vergleichstabellen
              </Link>
              <Link to="/" className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap">
                OOPBuy Deutschland Guide
              </Link>
              <Link to="/" className="text-xs bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full text-foreground-700 transition-colors whitespace-nowrap">
                OOPBuy FAQ
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}